import { useState } from 'react'
import type { StringListInputProps } from './StringListInput.types'
import './StringListInput.css'

// Free-text token editor: the operator types a value and adds it (Enter or the Add
// button). Entries render as removable chips. Blank and duplicate values are ignored,
// and adding is capped at `maxItems`. Used for sysEventIds on the rebuild form.
export function StringListInput({
  values,
  onChange,
  placeholder,
  addLabel = 'Add',
  ariaLabel = 'Add value',
  maxItems,
  isInvalid = false,
}: StringListInputProps) {
  const [draft, setDraft] = useState('')

  const isAtLimit = maxItems !== undefined && values.length >= maxItems

  function addDraft() {
    const trimmed = draft.trim()
    if (!trimmed || isAtLimit || values.includes(trimmed)) {
      setDraft('')
      return
    }
    onChange([...values, trimmed])
    setDraft('')
  }

  function removeAt(index: number) {
    onChange(values.filter((_, i) => i !== index))
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') {
      return
    }
    e.preventDefault()
    addDraft()
  }

  return (
    <div className="string-list-input w-100">
      {values.length > 0 && (
        <ul className="string-list-input-chips">
          {values.map((value, index) => (
            <li key={value} className="string-list-input-chip">
              <span className="string-list-input-chip-text">{value}</span>
              <button
                type="button"
                className="string-list-input-chip-remove"
                aria-label={`Remove ${value}`}
                onClick={() => removeAt(index)}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="string-list-input-add">
        <input
          type="text"
          className={`form-control form-control-sm${isInvalid ? ' is-invalid' : ''}`}
          value={draft}
          placeholder={placeholder}
          aria-label={ariaLabel}
          disabled={isAtLimit}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          disabled={isAtLimit || !draft.trim()}
          onClick={addDraft}
        >
          {addLabel}
        </button>
      </div>

      {isAtLimit && (
        <p className="string-list-input-limit">
          Maximum of {maxItems} reached.
        </p>
      )}
    </div>
  )
}
